import React, { useState, useEffect, useCallback } from 'react';
import { Brain, Plus, X, Edit2, Trash2, Sparkles, Info, Save, AlertCircle } from 'lucide-react';
import { useAIContext } from '../hooks/useAIContext';
import { useAuth } from '../hooks/useAuth';

const CATEGORIAS_EMPRESA = [
  { value: 'fato', label: '📋 Fato', hint: 'Informação objetiva (CNPJ, sede, tamanho)' },
  { value: 'relacionamento', label: '🤝 Relacionamento', hint: 'Rede, histórico pessoal' },
  { value: 'estrategia', label: '🎯 Estratégia', hint: 'Diretriz comercial (ex: oferecer permuta primeiro)' },
  { value: 'tom', label: '💬 Tom', hint: 'Estilo de comunicação' },
  { value: 'restricao', label: '🚫 Restrição', hint: 'Limites (nunca propor X)' },
  { value: 'observacao', label: '📌 Observação', hint: 'Genérico' },
];

const getCategoriaStyle = (cat) => {
  const map = {
    fato:           { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-800' },
    relacionamento: { bg: 'bg-purple-50',  border: 'border-purple-200',  text: 'text-purple-800' },
    estrategia:     { bg: 'bg-green-50',   border: 'border-green-200',   text: 'text-green-800' },
    tom:            { bg: 'bg-pink-50',    border: 'border-pink-200',    text: 'text-pink-800' },
    restricao:      { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-800' },
    observacao:     { bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-700' },
  };
  return map[cat] || map.observacao;
};

const getCategoriaLabel = (cat) => CATEGORIAS_EMPRESA.find(c => c.value === cat)?.label || cat;

/**
 * Painel de contexto da empresa — lista, cria, edita e remove notas.
 * Usado dentro do HistoryModal ou modal dedicado.
 */
export const AIContextPanelEmpresa = ({ empresaId, empresaNome, onClose }) => {
  const { user } = useAuth();
  const {
    fetchEmpresaContext,
    createEmpresaNote,
    updateEmpresaNote,
    deleteEmpresaNote,
  } = useAIContext();

  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);

  // Form de nova nota
  const [novaCategoria, setNovaCategoria] = useState('observacao');
  const [novoConteudo, setNovoConteudo] = useState('');
  const [salvandoNova, setSalvandoNova] = useState(false);

  // Edição inline
  const [editingId, setEditingId] = useState(null);
  const [editConteudo, setEditConteudo] = useState('');
  const [editCategoria, setEditCategoria] = useState('observacao');

  const carregar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const dados = await fetchEmpresaContext(empresaId);
      setNotas(dados);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [empresaId, fetchEmpresaContext]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleCriar = async () => {
    if (!novoConteudo.trim() || novoConteudo.trim().length < 3) {
      setError('Conteúdo precisa ter pelo menos 3 caracteres');
      return;
    }
    setSalvandoNova(true);
    setError(null);
    try {
      await createEmpresaNote(empresaId, {
        categoria: novaCategoria,
        conteudo: novoConteudo,
      }, user?.email);
      setNovoConteudo('');
      setNovaCategoria('observacao');
      await carregar();
    } catch (err) {
      setError(err.message);
    } finally {
      setSalvandoNova(false);
    }
  };

  const iniciarEdicao = (nota) => {
    setEditingId(nota.id);
    setEditConteudo(nota.conteudo);
    setEditCategoria(nota.categoria);
  };

  const cancelarEdicao = () => {
    setEditingId(null);
    setEditConteudo('');
    setEditCategoria('observacao');
  };

  const salvarEdicao = async (nota) => {
    if (!editConteudo.trim()) return;
    setSavingId(nota.id);
    setError(null);
    try {
      await updateEmpresaNote(
        nota.id,
        { conteudo: editConteudo.trim(), categoria: editCategoria },
        user?.email,
        nota
      );
      cancelarEdicao();
      await carregar();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId(null);
    }
  };

  const handleRemover = async (id) => {
    if (!window.confirm('Remover esta nota de contexto? Ela não aparecerá mais nos prompts IA.')) return;
    try {
      await deleteEmpresaNote(id);
      await carregar();
    } catch (err) {
      setError(err.message);
    }
  };

  // Agrupa por categoria para exibição
  const notasPorCategoria = notas.reduce((acc, n) => {
    if (!acc[n.categoria]) acc[n.categoria] = [];
    acc[n.categoria].push(n);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] 2xl:max-w-7xl max-h-[95vh] flex flex-col">

        {/* Header */}
        <div className="p-5 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-indigo-100 p-2 rounded-lg">
                <Brain className="text-indigo-600" size={22} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Contexto IA da Empresa</h2>
                <p className="text-xs text-slate-500 mt-0.5">{empresaNome}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X size={22} />
            </button>
          </div>

          <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-md p-2.5 text-xs text-indigo-800 flex items-start gap-1.5">
            <Info size={12} className="flex-shrink-0 mt-0.5" />
            <span>
              Estas notas alimentam TODO prompt IA sobre esta empresa (briefings, sugestões, detecções).
              É a memória operacional que a IA usa para pensar como um humano que conhece a empresa.
            </span>
          </div>
        </div>

        {/* Form nova nota */}
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex-shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <Plus size={14} className="text-slate-600" />
            <span className="text-xs font-semibold text-slate-700">Adicionar nota</span>
          </div>

          <div className="grid grid-cols-4 gap-2">
            <select
              value={novaCategoria}
              onChange={(e) => setNovaCategoria(e.target.value)}
              className="col-span-1 px-2 py-1.5 border border-slate-300 rounded text-xs"
              disabled={salvandoNova}
            >
              {CATEGORIAS_EMPRESA.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <textarea
              value={novoConteudo}
              onChange={(e) => setNovoConteudo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleCriar();
              }}
              placeholder="Ex: Denis é o decisor financeiro, Tarcísio é técnico. Sempre avaliam áreas na Faria Lima."
              className="col-span-3 px-2 py-1.5 border border-slate-300 rounded text-xs resize-none h-16"
              disabled={salvandoNova}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-slate-400">
              {CATEGORIAS_EMPRESA.find(c => c.value === novaCategoria)?.hint}
              {' · Ctrl+Enter salva'}
            </span>
            <button
              onClick={handleCriar}
              disabled={!novoConteudo.trim() || salvandoNova}
              className="px-3 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
            >
              <Plus size={11} /> {salvandoNova ? 'Salvando…' : 'Adicionar'}
            </button>
          </div>
        </div>

        {/* Lista de notas */}
        <div className="flex-grow overflow-y-auto p-4">
          {error && (
            <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800 flex items-start gap-1.5">
              <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {loading && (
            <div className="text-center py-8 text-sm text-slate-500">Carregando contexto…</div>
          )}

          {!loading && notas.length === 0 && (
            <div className="text-center py-10">
              <Brain size={32} className="mx-auto text-slate-300 mb-2" />
              <div className="text-sm text-slate-500">Nenhuma nota de contexto ainda.</div>
              <div className="text-xs text-slate-400 mt-1">
                Adicione notas acima para alimentar a IA com contexto sobre esta empresa.
              </div>
            </div>
          )}

          {!loading && notas.length > 0 && (
            <div className="space-y-4">
              {CATEGORIAS_EMPRESA.map(cat => {
                const notasDaCat = notasPorCategoria[cat.value];
                if (!notasDaCat || notasDaCat.length === 0) return null;
                const estilo = getCategoriaStyle(cat.value);

                return (
                  <div key={cat.value}>
                    <div className={`text-[11px] uppercase tracking-wider font-semibold ${estilo.text} mb-1.5`}>
                      {cat.label} ({notasDaCat.length})
                    </div>
                    <div className="space-y-1.5">
                      {notasDaCat.map(nota => {
                        const isEditing = editingId === nota.id;
                        const veioDeIA = nota.fonte === 'ai_inferido' || nota.fonte === 'ai_editado';
                        const foiEditado = nota.fonte === 'ai_editado';

                        return (
                          <div
                            key={nota.id}
                            className={`${estilo.bg} ${estilo.border} border rounded p-2.5 relative group`}
                          >
                            {veioDeIA && (
                              <div
                                className="absolute top-1.5 right-1.5 flex items-center gap-0.5"
                                title={foiEditado ? 'Editado por humano (original IA preservado)' : 'Gerado por IA'}
                              >
                                <Sparkles size={10} className={foiEditado ? 'text-amber-500' : 'text-purple-500'} />
                                {foiEditado && <Edit2 size={8} className="text-amber-500" />}
                              </div>
                            )}

                            {isEditing ? (
                              <div className="space-y-2">
                                <select
                                  value={editCategoria}
                                  onChange={(e) => setEditCategoria(e.target.value)}
                                  className="text-xs px-1.5 py-0.5 border border-slate-300 rounded w-full bg-white"
                                >
                                  {CATEGORIAS_EMPRESA.map(c => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                  ))}
                                </select>
                                <textarea
                                  value={editConteudo}
                                  onChange={(e) => setEditConteudo(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) salvarEdicao(nota);
                                    if (e.key === 'Escape') cancelarEdicao();
                                  }}
                                  rows={3}
                                  className="w-full text-xs px-2 py-1 border border-slate-300 rounded resize-none bg-white"
                                  autoFocus
                                />
                                <div className="flex justify-end gap-1">
                                  <button
                                    onClick={cancelarEdicao}
                                    className="px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-200 rounded"
                                  >
                                    Cancelar
                                  </button>
                                  <button
                                    onClick={() => salvarEdicao(nota)}
                                    disabled={savingId === nota.id}
                                    className="px-2 py-0.5 text-[11px] bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-0.5"
                                  >
                                    <Save size={10} />
                                    {savingId === nota.id ? 'Salvando…' : 'Salvar'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className={`text-xs ${estilo.text} whitespace-pre-wrap pr-8`}>
                                  {nota.conteudo}
                                </div>
                                <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-black/5">
                                  <div className="text-[10px] text-slate-500">
                                    {nota.updated_by || nota.created_by || 'sistema'} · {new Date(nota.updated_at || nota.created_at).toLocaleDateString('pt-BR')}
                                  </div>
                                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                                    <button
                                      onClick={() => iniciarEdicao(nota)}
                                      className="p-1 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                                      title="Editar"
                                    >
                                      <Edit2 size={11} />
                                    </button>
                                    <button
                                      onClick={() => handleRemover(nota.id)}
                                      className="p-1 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                                      title="Remover"
                                    >
                                      <Trash2 size={11} />
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-200 bg-slate-50 flex-shrink-0 flex items-center justify-between">
          <span className="text-[10px] text-slate-500">
            {notas.length} nota{notas.length !== 1 ? 's' : ''} ativa{notas.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={onClose}
            className="px-3 py-1 text-xs text-slate-600 hover:bg-slate-200 rounded"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIContextPanelEmpresa;
